import { AppDocumentLanguage } from '@ms/yammer-app-document-meta';
import { DeferredGlobalCopilotDrawer } from '@ms/yammer-copilot/dist/components/GlobalCopilot';
import { useDelegationStore } from '@ms/yammer-features-insights';
import { useLoadFeatureAccessInsights } from '@ms/yammer-hooks/dist/featureAccessInsights';
import { useLoadMultiTenantOrganization } from '@ms/yammer-hooks/dist/multiTenantOrganization';
import { useLoadStorylineSettings } from '@ms/yammer-hooks/dist/storylineSettings';
import { useComplianceChecks } from '@ms/yammer-hooks/dist/survey';
import { useLoadUserInformationBarrierSettings } from '@ms/yammer-hooks/dist/userInformationBarrierSettings';
import { useCurrentLocale } from '@ms/yammer-i18n-react';
import type { AppContextOptions } from '@ms/yammer-libs-app-context';
import { AppContextProvider } from '@ms/yammer-libs-app-context';
import { useGetActiveNetworkBrandTheme } from '@ms/yammer-network-brand-theme/dist/hooks';
import { LoadNetworkBrandTheme } from '@ms/yammer-network-brand-theme/dist/integration';
import { LoadableConfirmNavigationDialog } from '@ms/yammer-page-route-telemetry';
import { ReduxAnnouncerBridge } from '@ms/yammer-web-components/dist/accessibility/Announcer';
import FavoritesLimitReachedDialog from '@ms/yammer-web-components/dist/group/FavoritesLimitReachedDialog';
import type { LinkRenderingOptions } from '@ms/yammer-web-components/dist/link/YammerLink';
import { LinkRenderingOptionsProvider } from '@ms/yammer-web-components/dist/link/YammerLink';
import MediaLightbox from '@ms/yammer-web-components/dist/messageAttachments/MediaLightbox';
import { LoadableRealtimeClient } from '@ms/yammer-web-components/dist/realtime/RealtimeClient';
import SessionContextProviderWithHooks from '@ms/yammer-web-components/dist/session/SessionContextProviderWithHooks';
import { LoadableUsagePolicyDialog } from '@ms/yammer-web-components/dist/usagePolicy/UsagePolicyDialog';
import { SendPresenceState } from '@ms/yammer-web-components/dist/userPresence';
import AppContainer from '@ms/yammer-web-core-ui/dist/components/AppContainer';
import { LoadableToaster } from '@ms/yammer-web-core-ui/dist/fluent9/components/Toast';
import { BrandAccentColorContext } from '@ms/yammer-web-core-ui/dist/util/brandContext';
import { usePrepareIcons } from '@ms/yammer-web-core-ui/dist/util/icons';
import { ContainerWidthProvider } from '@ms/yammer-web-core-ui/dist/util/responsiveness';
import { getCoreUITokenOverridesForTeams } from '@ms/yammer-web-core-ui/dist/util/theme/fluent9';
import type { CoreUITokenOverrides } from '@ms/yammer-web-core-ui/dist/util/theme/fluent9';
import { isMobileEmbedView } from '@ms/yammer-web-support/dist/userAgent';
import { UIFeaturesProvider } from '@ms/yammer-web-ui-features/dist/features';
import TeamsClientProvider from 'components/TeamsClientProvider';
import {
  useDeepLinkPath,
  useIsMobileClient,
  useIsTeamsCached,
  useIsTeamsContrastTheme,
  useIsTeamsTab,
} from 'hooks/selectors';
import { useAppFeatureSettings } from 'hooks/useAppFeatureSettings';
import { useAppThemes } from 'hooks/useAppThemes';
import type { FC } from 'react';
import React, { useMemo } from 'react';

import AppLayout from '../AppLayout';
import Routes from '../Routes';

const brandColorOptions = {
  linksUseBrandColor: true,
};

const App: FC = () => {
  const themes = useAppThemes();
  const isMobileClient = useIsMobileClient();
  const isTeamsTab = useIsTeamsTab();
  const isTeamsContrastTheme = useIsTeamsContrastTheme();
  const features = useAppFeatureSettings();
  const locale = useCurrentLocale();
  const isCached = useIsTeamsCached();
  const complianceChecks = useComplianceChecks();
  const isFeedbackEnabled = complianceChecks.policyAllowFeedback !== 'Disabled';
  const deepLinkPath = useDeepLinkPath();
  const hasDeepLinkPath = !!deepLinkPath;
  const shouldDisableRealtime = hasDeepLinkPath || isCached;
  const isMobileEmbed = isMobileEmbedView();
  const { selectedOnBehalfOfUserId } = useDelegationStore();

  useLoadMultiTenantOrganization();
  useLoadFeatureAccessInsights({ selectedOnBehalfOfUserId });
  useLoadStorylineSettings();
  useLoadUserInformationBarrierSettings();

  usePrepareIcons();

  const teamsClientContextOptions = useMemo(
    () => ({
      collapseContentSidebar: false,
      shouldHideHeaders: isTeamsTab || isMobileEmbed,
      shouldHideLeftSidebar: isTeamsTab,
      shouldHideSubHeader: isTeamsTab || isMobileEmbed,
      shouldShowStaticAppHeader: false,
    }),
    [isTeamsTab, isMobileEmbed]
  );

  const appContextOptions: Partial<AppContextOptions> = useMemo(
    () => ({
      enableCollapsiblePanelNav: !isTeamsTab,
      disableVideoPlayer: isCached,
      disableLiveEventVideoPlayer: isCached,
    }),
    [isTeamsTab, isCached]
  );

  const linkRenderingOptions: LinkRenderingOptions = useMemo(
    () => ({
      disableLivePersonaCard: isMobileClient,
      shouldHideDownloadAttachmentLink: isMobileClient,
      shouldHideLegacyPageLinks: isMobileClient,
      shouldHideDescriptionMoreInfoLink: isMobileClient,
      shouldOpenDiscoverCommunitiesLinkInNewWindow: false,
      shouldOpenGroupHeaderSettingsLinkInNewWindow: true,
      shouldOpenGroupInfoLinkInNewWindow: false,
      shouldOpenGroupFilesInLegacyPage: true,
      shouldOpenGroupFilesInNewWindow: true,
      shouldOpenGroupDeleteInNewWindow: true,
      isThreadStarterCopyButtonLinkExternal: true,
      shouldOpenEventLinkInNewWindow: isTeamsTab,
      shouldOpenGroupLinkInNewWindow: isTeamsTab,
      shouldOpenThreadLinkInNewWindow: isTeamsTab,
      shouldOpenTagLinkInNewWindow: isTeamsTab,
      shouldOpenViewConversationLinkInNewWindow: isTeamsTab,
      shouldOpenYammerLinkInNewWindow: isTeamsTab,
    }),
    [isMobileClient, isTeamsTab]
  );

  const { currentActiveTheme, currentActiveThemeCoreUITokenOverrides, currentActiveThemeV9, isCustomThemingEnabled } =
    useGetActiveNetworkBrandTheme(themes.fluent8);
  const loadNetworkBrandTheme = isCustomThemingEnabled ? <LoadNetworkBrandTheme /> : null;
  const fluent9Theme = isCustomThemingEnabled && !isTeamsContrastTheme ? currentActiveThemeV9 : themes.fluent9;

  const teamsCoreUITokenOverrides = getCoreUITokenOverridesForTeams(currentActiveTheme.isInverted);
  const coreuiTokenOverrides: CoreUITokenOverrides = useMemo(
    () => ({
      ...currentActiveThemeCoreUITokenOverrides,
      ...teamsCoreUITokenOverrides,
    }),
    [currentActiveThemeCoreUITokenOverrides, teamsCoreUITokenOverrides]
  );

  return (
    <AppContainer
      theme={currentActiveTheme}
      coreuiTokenOverrides={coreuiTokenOverrides}
      fluent9Theme={fluent9Theme}
      emulateHighContrast={isTeamsContrastTheme}
      locale={locale}
    >
      <AppContextProvider mainColumnAriaRole="region" options={appContextOptions}>
        <UIFeaturesProvider features={features}>
          <SessionContextProviderWithHooks>
            <AppDocumentLanguage />
            <TeamsClientProvider options={teamsClientContextOptions}>
              <LinkRenderingOptionsProvider options={linkRenderingOptions}>
                <BrandAccentColorContext.Provider value={brandColorOptions}>
                  <ContainerWidthProvider>
                    <AppLayout>
                      <Routes />
                    </AppLayout>
                  </ContainerWidthProvider>
                  <LoadableUsagePolicyDialog />
                  <FavoritesLimitReachedDialog />
                  <MediaLightbox />
                  <DeferredGlobalCopilotDrawer isFeedbackEnabled={isFeedbackEnabled} />
                  <LoadableToaster />
                  <ReduxAnnouncerBridge />
                  <SendPresenceState />
                  <LoadableConfirmNavigationDialog />
                </BrandAccentColorContext.Provider>
              </LinkRenderingOptionsProvider>
            </TeamsClientProvider>
            <LoadableRealtimeClient isRealtimeDisabled={shouldDisableRealtime} />
          </SessionContextProviderWithHooks>
        </UIFeaturesProvider>
      </AppContextProvider>
      {loadNetworkBrandTheme}
    </AppContainer>
  );
};

export default App;
